import React, { useMemo } from 'react';
import { Copy, Check, Edit2, Send, Loader2, FileText, Paperclip, X, Wrench } from 'lucide-react';
import {
  ChatMessage,
  ChatMessageBubble,
  MarkdownContent,
  A2UISurfaceRenderer,
} from '../ChatComponents';
import ThoughtsPanel from './ThoughtsPanel';
import { SUPPORTED_ACCEPT_STRING, type AttachedFile } from '../../hooks/useFileAttachment';
import type { ThoughtStepInfo } from '../../hooks/useChatStream';
import type { ToolCallInfo } from '../../api';

interface ChatMessageRendererProps {
  msg: ChatMessage;
  msgIdx: number;
  isStreaming: boolean;
  lastToolUiMsgId: string | undefined;
  thoughtStep: ThoughtStepInfo | null | undefined;
  // Editing state
  editingMessageId: string | null | undefined;
  editingContent: string;
  onSetEditingContent: (content: string) => void;
  onStartEdit: (msg: ChatMessage) => void;
  onCancelEdit: () => void;
  onEditResend: () => void;
  // Copy state
  copiedMessageId: string | null | undefined;
  onCopy: (msg: ChatMessage) => void;
  // A2UI + send
  onSend: (overrideMessage?: string) => void;
  // File attachment in edit mode
  attachedFiles: AttachedFile[];
  onFileAttach: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  // Tool calls — full list, filtered by msg.id internally for the inline summary badge.
  toolCalls?: ToolCallInfo[];
  /** Called when the user clicks [자세히] on the tool summary badge.
   * Caller (PlaygroundChat) typically expands the ToolCallPanel. */
  onExpandToolPanel?: () => void;
}

const ChatMessageRenderer: React.FC<ChatMessageRendererProps> = React.memo(({
  msg,
  msgIdx,
  isStreaming,
  lastToolUiMsgId,
  thoughtStep,
  editingMessageId,
  editingContent,
  onSetEditingContent,
  onStartEdit,
  onCancelEdit,
  onEditResend,
  copiedMessageId,
  onCopy,
  onSend,
  attachedFiles,
  onFileAttach,
  onRemoveFile,
  toolCalls,
  onExpandToolPanel,
}) => {
  // Filter tools belonging to *this* assistant message only — multi-turn chats
  // would otherwise mix earlier tools into every message's badge.
  const msgTools = useMemo(
    () => (msg.role === 'assistant' && toolCalls ? toolCalls.filter((tc) => tc.messageId === msg.id) : []),
    [msg.id, msg.role, toolCalls],
  );
  const completedTools = msgTools.filter((tc) => tc.status === 'completed');
  return (
  <div
    data-msg-idx={msgIdx}
    data-testid={msg.role === 'assistant' ? 'assistant-message' : 'user-message'}
    className="group relative"
  >
    {/* Hover action bar */}
    {!isStreaming &&
      msg.content &&
      msg.source === 'active' &&
      editingMessageId !== msg.id && (
        <div
          className={`hidden group-hover:flex absolute -top-3 ${
            msg.role === 'user' ? 'right-14' : 'left-14'
          } z-10 items-center gap-1 bg-white border border-slate-200 rounded-lg shadow-sm px-1 py-0.5`}
        >
          <button
            onClick={() => onCopy(msg)}
            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="복사"
          >
            {copiedMessageId === msg.id ? (
              <Check size={14} className="text-green-500" />
            ) : (
              <Copy size={14} />
            )}
          </button>
          {msg.role === 'user' && (
            <button
              onClick={() => onStartEdit(msg)}
              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="수정"
            >
              <Edit2 size={14} />
            </button>
          )}
        </div>
      )}
    <ChatMessageBubble
      msg={msg}
      a2uiElement={
        msg.toolUi ? (
          <A2UISurfaceRenderer
            surface={msg.toolUi}
            isInteractive={msg.id === lastToolUiMsgId && !isStreaming}
            onSubmit={(response) => onSend(response)}
          />
        ) : undefined
      }
    >
      {/* Attached file previews */}
      {msg.files && msg.files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {msg.files.map((f, idx) => (
            <div key={idx} className="flex-shrink-0">
              {f.media_type.startsWith('image/') && f.preview ? (
                <img
                  src={f.preview}
                  alt={f.name}
                  className="w-24 h-24 object-cover rounded-lg border border-white/20"
                />
              ) : (
                <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2.5 py-1.5 text-xs">
                  <FileText size={14} />
                  <span className="max-w-[120px] truncate">{f.name}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Inline edit mode */}
      {editingMessageId === msg.id ? (
        <div className="space-y-2">
          <textarea
            value={editingContent}
            onChange={(e) => onSetEditingContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onEditResend();
              }
              if (e.key === 'Escape') onCancelEdit();
            }}
            className="w-full bg-blue-700/50 text-white border border-blue-400/30 rounded-lg p-2 resize-none focus:outline-none focus:border-blue-300 placeholder-blue-200"
            rows={3}
            autoFocus
          />
          {/* Attached files preview in edit mode */}
          {attachedFiles.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {attachedFiles.map((f, i) => (
                <div key={i} className="relative group/file">
                  {f.preview ? (
                    <img
                      src={f.preview}
                      alt={f.name}
                      className="w-16 h-16 object-cover rounded-lg border border-blue-400/30"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-blue-800/50 rounded-lg border border-blue-400/30 flex items-center justify-center text-xs text-blue-200">
                      {f.name.split('.').pop()}
                    </div>
                  )}
                  <button
                    onClick={() => onRemoveFile(i)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover/file:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between gap-2">
            <label className="px-3 py-1.5 text-xs text-blue-200 hover:text-white hover:bg-blue-700/50 rounded-lg transition-colors flex items-center gap-1 cursor-pointer">
              <Paperclip size={12} />
              파일
              <input
                type="file"
                className="hidden"
                accept={SUPPORTED_ACCEPT_STRING}
                multiple
                onChange={onFileAttach}
              />
            </label>
            <div className="flex gap-2">
              <button
                onClick={onCancelEdit}
                className="px-3 py-1.5 text-xs text-blue-200 hover:text-white hover:bg-blue-700/50 rounded-lg transition-colors flex items-center gap-1"
              >
                <X size={12} />
                취소
              </button>
              <button
                onClick={onEditResend}
                disabled={!editingContent.trim()}
                className="px-3 py-1.5 text-xs bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors flex items-center gap-1"
              >
                <Send size={12} />
                수정 &amp; 재전송
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="leading-relaxed prose-sm max-w-none">
          {msg.content ? (
            <MarkdownContent content={msg.content} isUser={msg.role === 'user'} />
          ) : msg.id === 'pending-response' ? (
            <span className="text-amber-500 italic flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              응답 생성 중... (백그라운드 처리)
            </span>
          ) : (
            <span className="text-slate-400 italic flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {thoughtStep ? (
                <>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-200 rounded font-mono text-slate-700">
                    {thoughtStep.step}
                  </span>
                  <span>{thoughtStep.content || '응답 중...'}</span>
                </>
              ) : (
                '응답 중...'
              )}
            </span>
          )}
        </div>
      )}
    </ChatMessageBubble>
    {/* Inline tool summary badge — visible regardless of side panel state.
        Spec §6 dashboard "메시지 하단 tool 요약 배지". */}
    {msg.role === 'assistant' && completedTools.length > 0 && !isStreaming && (
      <div
        className="flex items-center gap-2 mt-1 ml-14 text-xs text-slate-500 flex-wrap"
        data-testid="tool-summary-badge"
      >
        <Wrench size={12} className="text-slate-400" />
        <span>도구 {completedTools.length}개:</span>
        <span className="font-mono text-slate-600 flex items-center gap-1 flex-wrap">
          {completedTools.map((t, i) => {
            // Duration — prefer stored endTime; fall back to Date.now() for streaming edge cases.
            const dur = (t.endTime ?? Date.now()) - t.startTime;
            const durLabel = dur < 1000 ? `${dur}ms` : `${(dur / 1000).toFixed(1)}s`;
            return (
              <React.Fragment key={t.id}>
                <span>
                  {t.name}
                  <span className="text-slate-400">({durLabel})</span>
                  <span className="text-green-600 ml-0.5">✓</span>
                </span>
                {i < completedTools.length - 1 && <span className="text-slate-300">·</span>}
              </React.Fragment>
            );
          })}
        </span>
        {onExpandToolPanel && (
          <button
            onClick={onExpandToolPanel}
            className="text-blue-500 hover:text-blue-700 underline"
            data-testid="tool-summary-expand"
          >
            자세히
          </button>
        )}
      </div>
    )}
    {/* Gemini thinking chunks (admin Playground 한정 노출).
        opt-in fetch (`?include=thoughts`)된 메시지에만 표시. */}
    {msg.role === 'assistant' && !isStreaming && msg.thoughts && msg.thoughts.length > 0 && (
      <div className="ml-14 mt-1">
        <ThoughtsPanel thoughts={msg.thoughts} />
      </div>
    )}
  </div>
  );
});

ChatMessageRenderer.displayName = 'ChatMessageRenderer';

export default ChatMessageRenderer;
